import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { formatEURDecimal } from "../lib/format";
import { Users, FileText, BarChart3, Plus, Upload, Sparkles, Loader2, Trash2, Pencil, Save, Download, ExternalLink, Wand2, X, Info } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const eur = (v) => formatEURDecimal(v);

export default function Nominas() {
  const [tab, setTab] = useState("empleados");

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="nominas-page">
      <div className="mb-4">
        <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">RRHH</p>
        <h1 className="font-display text-3xl font-black tracking-tight">Nóminas</h1>
        <p className="text-sm text-[var(--ink-muted)] mt-1">Empleados, nóminas con IA y panel BI de costes.</p>
      </div>
      <div className="flex gap-1 border-b border-[var(--border)] mb-6">
        {[{v:"empleados", l:"Empleados", i:Users}, {v:"nominas", l:"Nóminas", i:FileText}, {v:"bi", l:"BI Costes", i:BarChart3}].map(o => {
          const Icon = o.i; const active = tab === o.v;
          return <button key={o.v} data-testid={`nom-tab-${o.v}`} onClick={() => setTab(o.v)} className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px ${active ? "border-black text-[var(--ink)] font-medium" : "border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]"}`}><Icon size={14}/> {o.l}</button>;
        })}
      </div>
      {tab === "empleados" && <EmpleadosTab/>}
      {tab === "nominas" && <NominasTab/>}
      {tab === "bi" && <BITab/>}
    </div>
  );
}

function EmpleadosTab() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const load = () => api.get("/empleados").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing.id) await api.put(`/empleados/${editing.id}`, editing);
      else await api.post("/empleados", editing);
      toast.success("Guardado"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => { if (!window.confirm("¿Borrar empleado?")) return; await api.delete(`/empleados/${id}`); load(); };

  const migrateFromProyectos = async () => {
    if (!window.confirm("Se importarán TODAS las nóminas históricas desde Proyectos (empleados + PDFs), por lotes de 20 para no cortar por timeout.\n\n¿Ejecutar ahora?")) return;
    setMigrating(true);
    let offset = 0;
    let totalImported = 0, totalPdfs = 0, totalEmpNew = 0, totalSkipped = 0, totalRounds = 0;
    const t = toast.loading("Iniciando migración...");
    try {
      while (true) {
        totalRounds++;
        const r = await api.post(`/integration/proyectos/migrate-nominas?offset=${offset}&limit=20`, null, { timeout: 60000 });
        const d = r.data || {};
        totalImported += (d.imported || 0);
        totalPdfs += (d.pdfs_saved || 0);
        totalEmpNew += (d.emp_created || 0);
        totalSkipped += (d.skipped || 0);
        toast.loading(`Progreso: ${d.processed || 0} / ${d.total || "?"} recibos · ${totalImported} importadas · ${totalPdfs} PDFs · ${totalEmpNew} empleados nuevos`, { id: t });
        if (!d.next_offset) break;
        offset = d.next_offset;
        if (totalRounds > 50) throw new Error("Demasiadas iteraciones, corta manualmente");
        // pequeño respiro entre lotes
        await new Promise(res => setTimeout(res, 300));
      }
      toast.dismiss(t);
      toast.success(`Migración completa: ${totalImported} nóminas · ${totalPdfs} PDFs · ${totalEmpNew} empleados nuevos${totalSkipped?` · ${totalSkipped} ya existían`:""}`, { duration: 10000 });
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || e?.message || "Error en la migración");
    } finally { setMigrating(false); }
  };

  const syncEmpleadosPuestos = async () => {
    if (!window.confirm("Se traerán los empleados desde Proyectos y se enriquecerán los puestos vacíos. No se pisan los que ya rellenaste.\n\n¿Continuar?")) return;
    setMigrating(true);
    const t = toast.loading("Sincronizando empleados desde Proyectos...");
    try {
      const r = await api.post("/integration/proyectos/sync-empleados", null, { timeout: 60000 });
      const d = r.data || {};
      toast.dismiss(t);
      toast.success(`Sync empleados: ${d.updated_puesto} puestos actualizados · ${d.created} nuevos · ${d.matched} coincidencias`, { duration: 8000 });
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || e?.message || "Error sincronizando");
    } finally { setMigrating(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button data-testid="emp-new" onClick={() => setEditing({ nombre: "", dni: "", email: "", puesto: "", fecha_alta: "", activo: true, notas: "", numero_empleado: "", tipo_contrato: "Indefinido", jornada: "Completa", salario_bruto_anual: 0, num_pagas: 14 })} className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded"><Plus size={14}/> Nuevo empleado</button>
        <button data-testid="emp-migrate" onClick={migrateFromProyectos} disabled={migrating} className="inline-flex items-center gap-2 border bg-white text-sm px-3 py-2 rounded hover:bg-[var(--bg)] disabled:opacity-40">
          {migrating ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Migrar histórico desde Proyectos
        </button>
        <button data-testid="emp-sync-puestos" onClick={syncEmpleadosPuestos} disabled={migrating} className="inline-flex items-center gap-2 border bg-white text-sm px-3 py-2 rounded hover:bg-[var(--bg)] disabled:opacity-40">
          <Sparkles size={14}/> Sincronizar puestos
        </button>
        {list.length === 0 && !migrating && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Sin empleados en este entorno — pulsa "Migrar histórico" para traer los 123 recibos desde Proyectos.</span>
        )}
      </div>
      {editing && (
        <div className="bg-white border rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <F l="Nombre"><input value={editing.nombre} onChange={e=>setEditing({...editing, nombre:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-nombre"/></F>
            <F l="DNI"><input value={editing.dni||""} onChange={e=>setEditing({...editing, dni:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-dni"/></F>
            <F l="Email"><input type="email" value={editing.email||""} onChange={e=>setEditing({...editing, email:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-email"/></F>
            <F l="Puesto"><input value={editing.puesto||""} onChange={e=>setEditing({...editing, puesto:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="Activo"><select value={editing.activo?"si":"no"} onChange={e=>setEditing({...editing, activo:e.target.value==="si"})} className="w-full border rounded px-2 py-1.5"><option value="si">Sí</option><option value="no">No</option></select></F>
          </div>
          <div className="mt-3 pt-3 border-t">
            <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-mono-num mb-2 flex items-center gap-2">
              Datos laborales <span className="text-[9px] text-violet-600 font-normal">(visibles en el Portal de Proyectos)</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <F l="Nº empleado"><input value={editing.numero_empleado||""} onChange={e=>setEditing({...editing, numero_empleado:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-numero"/></F>
              <F l="Fecha alta"><input type="date" value={editing.fecha_alta||""} onChange={e=>setEditing({...editing, fecha_alta:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
              <F l="Tipo contrato">
                <select value={editing.tipo_contrato||""} onChange={e=>setEditing({...editing, tipo_contrato:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-tipo-contrato">
                  <option value="">—</option>
                  <option value="Indefinido">Indefinido</option>
                  <option value="Temporal">Temporal</option>
                  <option value="Formación">Formación</option>
                  <option value="Prácticas">Prácticas</option>
                  <option value="Obra y servicio">Obra y servicio</option>
                  <option value="Autónomo">Autónomo</option>
                </select>
              </F>
              <F l="Jornada">
                <select value={editing.jornada||""} onChange={e=>setEditing({...editing, jornada:e.target.value})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-jornada">
                  <option value="">—</option>
                  <option value="Completa">Completa</option>
                  <option value="Parcial 75%">Parcial 75%</option>
                  <option value="Parcial 50%">Parcial 50%</option>
                  <option value="Parcial 25%">Parcial 25%</option>
                </select>
              </F>
              <F l="Nº pagas">
                <select value={editing.num_pagas ?? 12} onChange={e=>setEditing({...editing, num_pagas:parseInt(e.target.value)})} className="w-full border rounded px-2 py-1.5" data-testid="emp-form-num-pagas">
                  <option value="12">12</option>
                  <option value="14">14</option>
                  <option value="15">15</option>
                  <option value="16">16</option>
                </select>
              </F>
              <F l="Salario bruto anual (€)" span="md:col-span-2">
                <input type="number" step="100" value={editing.salario_bruto_anual ?? 0} onChange={e=>setEditing({...editing, salario_bruto_anual:parseFloat(e.target.value)||0})} className="w-full border rounded px-2 py-1.5 font-mono-num" data-testid="emp-form-salario"/>
              </F>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={()=>setEditing(null)} className="px-3 py-2 border rounded text-sm">Cancelar</button>
            <button data-testid="emp-save" onClick={save} disabled={!editing.nombre.trim()} className="inline-flex items-center gap-2 bg-black text-white text-sm px-4 py-2 rounded disabled:opacity-40"><Save size={14}/> Guardar</button>
          </div>
        </div>
      )}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
            <tr><th className="text-left px-3 py-2">Nombre</th><th className="text-left px-3 py-2">DNI</th><th className="text-left px-3 py-2">Puesto</th><th className="text-left px-3 py-2">Alta</th><th className="text-left px-3 py-2">Estado</th><th></th></tr>
          </thead>
          <tbody>
            {list.length===0 && <tr><td colSpan={6} className="text-center text-[var(--ink-muted)] py-6">Sin empleados. Añade el primero.</td></tr>}
            {list.map(e => (
              <tr key={e.id} className="border-t hover:bg-[var(--hover)]">
                <td className="px-3 py-2 font-medium">
                  <Link to={`/rrhh/${e.id}`} className="hover:underline inline-flex items-center gap-1" data-testid={`emp-link-${e.id}`}>
                    {e.nombre} <ExternalLink size={11} className="text-[var(--ink-muted)]"/>
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs font-mono-num">{e.dni||"—"}</td>
                <td className="px-3 py-2">{e.puesto||"—"}</td>
                <td className="px-3 py-2 text-xs">{e.fecha_alta||"—"}</td>
                <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${e.activo?"bg-emerald-100 text-emerald-800":"bg-gray-200 text-gray-600"}`}>{e.activo?"Activo":"Baja"}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Link to={`/rrhh/${e.id}`} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)] inline-flex"><BarChart3 size={13}/></Link>
                  <button onClick={()=>setEditing(e)} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"><Pencil size={13}/></button>
                  <button onClick={()=>remove(e.id)} className="p-1 text-[var(--ink-muted)] hover:text-rose-600"><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NominasTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [empleados, setEmpleados] = useState([]);
  const [nominas, setNominas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewItems, setReviewItems] = useState(null); // array de nóminas extraídas por IA pendientes de revisar/guardar
  const [savingBulk, setSavingBulk] = useState(false);
  const [showPrenomina, setShowPrenomina] = useState(false);
  const fileRef = useRef(null);

  const load = () => Promise.all([
    api.get("/empleados").then(r => setEmpleados(r.data)),
    api.get(`/nominas?year=${year}`).then(r => setNominas(r.data)),
  ]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/nominas/extract-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      const ps = r.data?.payslips || [];
      if (ps.length === 0) { toast.error("No se detectó ninguna nómina"); return; }
      // Preparar review con defaults inteligentes
      setReviewItems(ps.map(p => ({
        empleado_id: p.empleado_id_sugerido || "",
        empleado_nombre_ia: p.nombre_empleado || "",
        mes: p.mes || new Date().getMonth() + 1,
        year: p.year || year,
        bruto: p.bruto || 0,
        neto: p.neto || 0,
        irpf: p.irpf || 0,
        ss_trabajador: p.ss_trabajador || 0,
        ss_empresa: p.ss_empresa || 0,
        pdf_id: p.pdf_id || "",
        page_from: p.page_from,
        page_to: p.page_to,
        notas: p.nombre_empleado ? `IA: ${p.nombre_empleado} (pág. ${p.page_from}-${p.page_to})` : "",
      })));
      const nMatched = ps.filter(p => p.empleado_id_sugerido).length;
      toast.success(`IA detectó ${ps.length} nómina${ps.length!==1?"s":""} (${nMatched} empleado${nMatched!==1?"s":""} identificado${nMatched!==1?"s":""}). Revisa y guarda.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error extrayendo PDF"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveReviewBulk = async () => {
    const valid = reviewItems.filter(r => r.empleado_id && r.mes && r.year);
    if (valid.length === 0) { toast.error("Ninguna fila válida (asigna empleado)"); return; }
    setSavingBulk(true);
    try {
      const payload = valid.map(r => ({
        empleado_id: r.empleado_id,
        mes: parseInt(r.mes), year: parseInt(r.year),
        bruto: parseFloat(r.bruto)||0, neto: parseFloat(r.neto)||0, irpf: parseFloat(r.irpf)||0,
        ss_trabajador: parseFloat(r.ss_trabajador)||0, ss_empresa: parseFloat(r.ss_empresa)||0,
        pdf_id: r.pdf_id || "", notas: r.notas || "",
      }));
      const r = await api.post("/nominas/bulk-save", payload);
      toast.success(`${r.data.created} nómina${r.data.created!==1?"s":""} guardada${r.data.created!==1?"s":""}`);
      setReviewItems(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error guardando"); }
    finally { setSavingBulk(false); }
  };

  const save = async () => {
    try {
      const payload = {
        ...editing,
        mes: parseInt(editing.mes), year: parseInt(editing.year),
        bruto: parseFloat(editing.bruto)||0, neto: parseFloat(editing.neto)||0, irpf: parseFloat(editing.irpf)||0,
        ss_trabajador: parseFloat(editing.ss_trabajador)||0, ss_empresa: parseFloat(editing.ss_empresa)||0,
      };
      payload.coste_empresa_total = payload.bruto + payload.ss_empresa;
      if (editing.id) await api.put(`/nominas/${editing.id}`, payload);
      else await api.post("/nominas", payload);
      toast.success("Nómina guardada"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => { if (!window.confirm("¿Borrar nómina?")) return; await api.delete(`/nominas/${id}`); load(); };
  const empName = (id) => (empleados.find(e => e.id === id)?.nombre) || "—";
  const downloadPdf = async (n) => {
    try {
      const r = await api.get(`/nominas/${n.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `nomina_${empName(n.empleado_id).replace(/ /g,"_")}_${n.year}_${String(n.mes).padStart(2,"0")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sin PDF"); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border rounded px-3 py-2 text-sm">{[year-1,year,year+1].map(y=><option key={y}>{y}</option>)}</select>
        <input ref={fileRef} type="file" accept=".pdf" onChange={onUpload} className="hidden"/>
        <button data-testid="nom-upload" onClick={()=>fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border bg-white text-sm px-3 py-2 rounded disabled:opacity-40">{uploading?<Loader2 size={14} className="animate-spin"/>:<><Sparkles size={14}/><Upload size={14}/></>} Subir PDF (IA · multi‑empleado)</button>
        <button data-testid="nom-new" onClick={()=>setEditing({empleado_id: empleados[0]?.id || "", mes: new Date().getMonth()+1, year, bruto:0, neto:0, irpf:0, ss_trabajador:0, ss_empresa:0, coste_empresa_total:0, notas:""})} className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded"><Plus size={14}/> Manual</button>
        <button data-testid="prenomina-open" onClick={()=>setShowPrenomina(true)} className="inline-flex items-center gap-2 bg-violet-600 text-white text-sm px-3 py-2 rounded hover:bg-violet-700"><Wand2 size={14}/> Generar Prenómina IA</button>
        {uploading && <span className="text-xs text-[var(--ink-muted)]">Analizando PDF con IA (puede tardar 30-90s)…</span>}
      </div>

      {showPrenomina && <PrenominaModal empleados={empleados} defaultYear={year} onClose={()=>{ setShowPrenomina(false); load(); }}/>}

      {reviewItems && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="review-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-lg font-black">Revisar {reviewItems.length} nómina{reviewItems.length!==1?"s":""} extraída{reviewItems.length!==1?"s":""}</h3>
              <p className="text-xs text-[var(--ink-muted)]">Asigna el empleado y comprueba los importes antes de guardar. Cada fila se guardará con su PDF individual adjunto.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={()=>setReviewItems(null)} className="px-3 py-2 border bg-white rounded text-sm">Descartar</button>
              <button data-testid="review-save-all" onClick={saveReviewBulk} disabled={savingBulk} className="inline-flex items-center gap-2 bg-black text-white text-sm px-4 py-2 rounded disabled:opacity-40">{savingBulk?<Loader2 size={14} className="animate-spin"/>:<Save size={14}/>} Guardar todas</button>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-white text-[10px] uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
                <tr>
                  <th className="text-left px-2 py-2 w-8">#</th>
                  <th className="text-left px-2 py-2">Pág.</th>
                  <th className="text-left px-2 py-2">IA extrajo</th>
                  <th className="text-left px-2 py-2 min-w-[180px]">Empleado</th>
                  <th className="text-left px-2 py-2">Mes</th>
                  <th className="text-left px-2 py-2">Año</th>
                  <th className="text-right px-2 py-2">Bruto</th>
                  <th className="text-right px-2 py-2">IRPF</th>
                  <th className="text-right px-2 py-2">SS Trab</th>
                  <th className="text-right px-2 py-2">Neto</th>
                  <th className="text-right px-2 py-2">SS Empr</th>
                  <th className="text-right px-2 py-2">Coste emp.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reviewItems.map((r, i) => {
                  const upd = (k, v) => setReviewItems(prev => prev.map((x, idx) => idx===i ? {...x, [k]: v} : x));
                  const coste = (parseFloat(r.bruto)||0) + (parseFloat(r.ss_empresa)||0);
                  const noEmp = !r.empleado_id;
                  return (
                    <tr key={i} className={`border-t ${noEmp?"bg-rose-50":"bg-white"}`}>
                      <td className="px-2 py-1 text-[var(--ink-muted)]">{i+1}</td>
                      <td className="px-2 py-1 text-[var(--ink-muted)] font-mono-num">{r.page_from}-{r.page_to}</td>
                      <td className="px-2 py-1 text-[var(--ink-muted)] italic">{r.empleado_nombre_ia||"—"}</td>
                      <td className="px-2 py-1">
                        <select data-testid={`review-emp-${i}`} value={r.empleado_id} onChange={e=>upd("empleado_id", e.target.value)} className={`w-full border rounded px-1.5 py-1 ${noEmp?"border-rose-400":""}`}>
                          <option value="">— seleccionar —</option>
                          {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1"><select value={r.mes} onChange={e=>upd("mes", parseInt(e.target.value))} className="w-full border rounded px-1.5 py-1">{MESES.map((m,idx)=><option key={idx+1} value={idx+1}>{m}</option>)}</select></td>
                      <td className="px-2 py-1"><input type="number" value={r.year} onChange={e=>upd("year", e.target.value)} className="w-20 border rounded px-1.5 py-1"/></td>
                      <td className="px-2 py-1"><input type="number" step="0.01" value={r.bruto} onChange={e=>upd("bruto", e.target.value)} className="w-24 border rounded px-1.5 py-1 text-right font-mono-num"/></td>
                      <td className="px-2 py-1"><input type="number" step="0.01" value={r.irpf} onChange={e=>upd("irpf", e.target.value)} className="w-20 border rounded px-1.5 py-1 text-right font-mono-num"/></td>
                      <td className="px-2 py-1"><input type="number" step="0.01" value={r.ss_trabajador} onChange={e=>upd("ss_trabajador", e.target.value)} className="w-20 border rounded px-1.5 py-1 text-right font-mono-num"/></td>
                      <td className="px-2 py-1"><input type="number" step="0.01" value={r.neto} onChange={e=>upd("neto", e.target.value)} className="w-24 border rounded px-1.5 py-1 text-right font-mono-num"/></td>
                      <td className="px-2 py-1"><input type="number" step="0.01" value={r.ss_empresa} onChange={e=>upd("ss_empresa", e.target.value)} className="w-20 border rounded px-1.5 py-1 text-right font-mono-num"/></td>
                      <td className="px-2 py-1 text-right font-mono-num font-semibold text-rose-700">{eur(coste)}</td>
                      <td className="px-2 py-1 text-right">
                        <button onClick={()=>setReviewItems(prev => prev.filter((_,idx)=>idx!==i))} className="p-1 text-[var(--ink-muted)] hover:text-rose-600"><Trash2 size={13}/></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <div className="bg-white border rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-8 gap-3 text-sm">
            <F l="Empleado" span="md:col-span-2"><select value={editing.empleado_id} onChange={e=>setEditing({...editing, empleado_id:e.target.value})} className="w-full border rounded px-2 py-1.5"><option value="">— seleccionar —</option>{empleados.map(e=><option key={e.id} value={e.id}>{e.nombre}</option>)}</select></F>
            <F l="Mes"><select value={editing.mes} onChange={e=>setEditing({...editing, mes:parseInt(e.target.value)})} className="w-full border rounded px-2 py-1.5">{MESES.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}</select></F>
            <F l="Año"><input type="number" value={editing.year} onChange={e=>setEditing({...editing, year:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="Bruto"><input type="number" step="0.01" value={editing.bruto} onChange={e=>setEditing({...editing, bruto:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="IRPF"><input type="number" step="0.01" value={editing.irpf} onChange={e=>setEditing({...editing, irpf:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="SS Trab."><input type="number" step="0.01" value={editing.ss_trabajador} onChange={e=>setEditing({...editing, ss_trabajador:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="Neto"><input type="number" step="0.01" value={editing.neto} onChange={e=>setEditing({...editing, neto:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="SS Empresa" span="md:col-span-2"><input type="number" step="0.01" value={editing.ss_empresa} onChange={e=>setEditing({...editing, ss_empresa:e.target.value})} className="w-full border rounded px-2 py-1.5"/></F>
            <F l="Coste empresa total (auto)" span="md:col-span-2"><input type="number" step="0.01" value={(parseFloat(editing.bruto)||0)+(parseFloat(editing.ss_empresa)||0)} disabled className="w-full border rounded px-2 py-1.5 bg-[var(--bg)] font-semibold"/></F>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={()=>setEditing(null)} className="px-3 py-2 border rounded text-sm">Cancelar</button>
            <button data-testid="nom-save" onClick={save} disabled={!editing.empleado_id} className="inline-flex items-center gap-2 bg-black text-white text-sm px-4 py-2 rounded disabled:opacity-40"><Save size={14}/> Guardar</button>
          </div>
        </div>
      )}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
            <tr><th className="text-left px-3 py-2">Mes</th><th className="text-left px-3 py-2">Empleado</th><th className="text-right px-3 py-2">Bruto</th><th className="text-right px-3 py-2">IRPF</th><th className="text-right px-3 py-2">SS Trab</th><th className="text-right px-3 py-2">Neto</th><th className="text-right px-3 py-2">SS Empr</th><th className="text-right px-3 py-2">Coste Empresa</th><th></th></tr>
          </thead>
          <tbody>
            {nominas.length===0 && <tr><td colSpan={9} className="text-center text-[var(--ink-muted)] py-6">Sin nóminas en {year}. Sube un PDF o añade manual.</td></tr>}
            {nominas.map(n => (
              <tr key={n.id} className="border-t hover:bg-[var(--hover)]">
                <td className="px-3 py-2 font-mono-num text-xs">{MESES[(n.mes||1)-1]} {n.year}</td>
                <td className="px-3 py-2">
                  <Link to={`/rrhh/${n.empleado_id}`} className="hover:underline">{empName(n.empleado_id)}</Link>
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{eur(n.bruto)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-[var(--ink-muted)]">{eur(n.irpf)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-[var(--ink-muted)]">{eur(n.ss_trabajador)}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold">{eur(n.neto)}</td>
                <td className="px-3 py-2 text-right font-mono-num">{eur(n.ss_empresa)}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold text-rose-700">{eur(n.coste_empresa_total)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {n.pdf_id && <button title="Descargar PDF" onClick={()=>downloadPdf(n)} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"><Download size={13}/></button>}
                  <button onClick={()=>setEditing(n)} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"><Pencil size={13}/></button>
                  <button onClick={()=>remove(n.id)} className="p-1 text-[var(--ink-muted)] hover:text-rose-600"><Trash2 size={13}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BITab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/nominas/bi?year=${year}`).then(r => setData(r.data)); }, [year]);
  if (!data) return <div className="text-center text-sm text-[var(--ink-muted)] py-8">Cargando…</div>;
  const chartData = data.mensual.map(m => ({ mes: MESES[m.mes-1], "Coste Empresa": m.coste_empresa, "Bruto": m.bruto, "Neto": m.neto }));
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <select value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border rounded px-3 py-2 text-sm">{[year-1,year,year+1].map(y=><option key={y}>{y}</option>)}</select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BIBox label="Bruto anual" value={eur(data.total_year.bruto)} color="ink"/>
        <BIBox label="Neto anual" value={eur(data.total_year.neto)} color="emerald"/>
        <BIBox label="Coste Empresa" value={eur(data.total_year.coste_empresa)} color="rose"/>
        <BIBox label="Empleados activos" value={data.n_empleados_activos} color="ink"/>
      </div>
      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-display text-lg font-black mb-4">Coste RRHH mensual</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="mes" fontSize={10}/>
            <YAxis fontSize={10} tickFormatter={v=>new Intl.NumberFormat("es-ES",{notation:"compact"}).format(v)}/>
            <Tooltip formatter={v=>eur(v)}/>
            <Legend wrapperStyle={{fontSize:11}}/>
            <Bar dataKey="Bruto" fill="#111"/>
            <Bar dataKey="Neto" fill="#10b981"/>
            <Bar dataKey="Coste Empresa" fill="#ef4444"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white border rounded-lg p-5">
        <h3 className="font-display text-lg font-black mb-3">Por empleado ({year})</h3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-[var(--ink-muted)] font-mono-num tracking-wider bg-[var(--bg)]">
            <tr><th className="text-left px-3 py-2">Empleado</th><th className="text-left px-3 py-2">Puesto</th><th className="text-right px-3 py-2">Bruto año</th><th className="text-right px-3 py-2">Coste Empresa año</th><th className="text-right px-3 py-2">Meses con nómina</th></tr>
          </thead>
          <tbody>
            {data.por_empleado.length===0 && <tr><td colSpan={5} className="text-center text-[var(--ink-muted)] py-6">Sin datos.</td></tr>}
            {data.por_empleado.map(e => (
              <tr key={e.empleado_id} className="border-t">
                <td className="px-3 py-2 font-medium">
                  <Link to={`/rrhh/${e.empleado_id}`} className="hover:underline">{e.nombre}</Link>
                </td>
                <td className="px-3 py-2 text-[var(--ink-muted)]">{e.puesto||"—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{eur(e.total_bruto_year)}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold text-rose-700">{eur(e.total_coste_empresa_year)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs text-[var(--ink-muted)]">{e.meses.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ l, children, span }) {
  return <div className={span||""}><label className="block text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mb-1 font-mono-num">{l}</label>{children}</div>;
}
function BIBox({ label, value, color }) {
  const colorMap = { emerald: "text-emerald-700", rose: "text-rose-700", ink: "text-[var(--ink)]" };
  return <div className="bg-white border rounded-lg p-4"><div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num">{label}</div><div className={`mt-1 font-mono-num text-2xl font-bold ${colorMap[color]}`}>{value}</div></div>;
}

function PrenominaModal({ empleados, defaultYear, onClose }) {
  const activos = useMemo(() => empleados.filter(e => e.activo), [empleados]);
  const now = new Date();
  const [year, setYear] = useState(defaultYear || now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 2 > 12 ? 1 : now.getMonth() + 2); // mes siguiente
  const [notasGlob, setNotasGlob] = useState("");
  // ajustes por empleado: { [empleado_id]: { pct, bonus, baja } }
  const [adj, setAdj] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const setEmpAdj = (eid, patch) => setAdj(prev => ({ ...prev, [eid]: { ...(prev[eid] || {}), ...patch } }));

  const generate = async () => {
    setLoading(true); setResult(null);
    const adjustments = Object.entries(adj)
      .filter(([_, v]) => (v.pct && parseFloat(v.pct) !== 0) || (v.bonus && parseFloat(v.bonus) !== 0) || v.baja)
      .map(([empleado_id, v]) => ({
        empleado_id,
        pct: parseFloat(v.pct) || 0,
        bonus: parseFloat(v.bonus) || 0,
        baja: !!v.baja,
      }));
    try {
      const r = await api.post("/prenomina/generate", { year: parseInt(year), mes: parseInt(mes), adjustments, notas_globales: notasGlob });
      setResult(r.data);
      toast.success(`Prenómina generada: ${r.data.count} empleados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando prenómina");
    } finally { setLoading(false); }
  };

  const discardDraft = async () => {
    if (!window.confirm("¿Borrar el borrador guardado para este mes?")) return;
    try {
      await api.delete(`/prenomina?year=${year}&mes=${mes}`);
      toast.success("Borrador eliminado");
      setResult(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" data-testid="prenomina-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl my-8">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <Wand2 size={18} className="text-violet-600"/>
            <h2 className="font-display text-xl font-black">Prenómina IA</h2>
            <span className="text-xs text-[var(--ink-muted)]">borrador previo a la nómina real</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--hover)] rounded" data-testid="prenomina-close"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Parámetros generales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <F l="Mes previsión">
              <select value={mes} onChange={e=>setMes(parseInt(e.target.value))} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="prenomina-mes">
                {MESES.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </F>
            <F l="Año">
              <select value={year} onChange={e=>setYear(parseInt(e.target.value))} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="prenomina-year">
                {[year-1, year, year+1].map(y=><option key={y}>{y}</option>)}
              </select>
            </F>
            <F l="Notas para la IA" span="md:col-span-2">
              <input value={notasGlob} onChange={e=>setNotasGlob(e.target.value)} placeholder="Ej: alta de Ana el día 15, sin paga extra…" className="w-full border rounded px-2 py-1.5 text-sm"/>
            </F>
          </div>

          {/* Tabla de ajustes por empleado */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-[var(--bg)] px-3 py-2 text-xs font-mono-num uppercase text-[var(--ink-muted)] flex items-center gap-2">
              <Info size={12}/> Ajustes por empleado ({activos.length} activos) — deja en 0 si no cambia
            </div>
            <div className="overflow-auto max-h-[300px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white text-[10px] uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2">Empleado</th>
                    <th className="text-right px-3 py-2 w-24">Subida %</th>
                    <th className="text-right px-3 py-2 w-28">Bonus €</th>
                    <th className="text-center px-3 py-2 w-16">Baja</th>
                  </tr>
                </thead>
                <tbody>
                  {activos.map(e => {
                    const a = adj[e.id] || {};
                    return (
                      <tr key={e.id} className="border-t hover:bg-[var(--hover)]">
                        <td className="px-3 py-1.5 text-sm">{e.nombre}</td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="0.1" placeholder="0" value={a.pct||""} onChange={ev=>setEmpAdj(e.id,{pct:ev.target.value})} className="w-20 border rounded px-2 py-1 text-right text-sm font-mono-num" data-testid={`prenomina-pct-${e.id}`} disabled={a.baja}/>
                        </td>
                        <td className="px-3 py-1 text-right">
                          <input type="number" step="1" placeholder="0" value={a.bonus||""} onChange={ev=>setEmpAdj(e.id,{bonus:ev.target.value})} className="w-24 border rounded px-2 py-1 text-right text-sm font-mono-num" data-testid={`prenomina-bonus-${e.id}`} disabled={a.baja}/>
                        </td>
                        <td className="px-3 py-1 text-center">
                          <input type="checkbox" checked={!!a.baja} onChange={ev=>setEmpAdj(e.id,{baja:ev.target.checked})} data-testid={`prenomina-baja-${e.id}`}/>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Botón generar */}
          <div className="flex items-center justify-end gap-2">
            {result && <button onClick={discardDraft} className="text-xs text-rose-600 hover:underline mr-auto" data-testid="prenomina-discard">Borrar este borrador</button>}
            <button onClick={onClose} className="px-3 py-2 border rounded text-sm">Cerrar</button>
            <button onClick={generate} disabled={loading} className="inline-flex items-center gap-2 bg-violet-600 text-white text-sm px-4 py-2 rounded hover:bg-violet-700 disabled:opacity-40" data-testid="prenomina-generate">
              {loading ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Generar prenómina
            </button>
          </div>

          {/* Resultado */}
          {result && (
            <div className="border-t pt-5 space-y-4" data-testid="prenomina-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <BIBox label="Coste empresa previsto" value={eur(result.totales.coste_empresa)} color="rose"/>
                <BIBox label="Bruto total" value={eur(result.totales.bruto)} color="ink"/>
                <BIBox label="Neto total" value={eur(result.totales.neto)} color="emerald"/>
                <BIBox
                  label={`vs ${MESES[(result.comparativa_mes_anterior.mes||1)-1]} ${result.comparativa_mes_anterior.year}`}
                  value={`${result.comparativa_mes_anterior.delta_pct>=0?"+":""}${result.comparativa_mes_anterior.delta_pct.toFixed(1)}%`}
                  color={Math.abs(result.comparativa_mes_anterior.delta_pct) < 5 ? "ink" : "rose"}
                />
              </div>

              {result.comentario_ia && (
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Sparkles size={16} className="text-violet-600 shrink-0 mt-0.5"/>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-violet-700 font-mono-num mb-1">Análisis IA (Gemini)</div>
                      <p className="text-sm text-[var(--ink)] whitespace-pre-wrap leading-relaxed">{result.comentario_ia}</p>
                    </div>
                  </div>
                </div>
              )}

              {result.empleados_sin_referencia?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                  <strong>{result.empleados_sin_referencia.length} empleados sin referencia</strong> (no tienen nóminas anteriores):
                  {" "}{result.empleados_sin_referencia.map(e=>e.nombre).join(", ")}. No aparecen en el borrador.
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-[var(--bg)] px-3 py-2 text-xs font-mono-num uppercase text-[var(--ink-muted)]">
                  Detalle por empleado ({result.count})
                </div>
                <div className="overflow-auto max-h-[350px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white text-[10px] uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
                      <tr className="border-b">
                        <th className="text-left px-3 py-2">Empleado</th>
                        <th className="text-left px-3 py-2">Base ref</th>
                        <th className="text-right px-3 py-2">Bruto</th>
                        <th className="text-right px-3 py-2">Neto</th>
                        <th className="text-right px-3 py-2">SS Empr</th>
                        <th className="text-right px-3 py-2">Coste</th>
                        <th className="text-right px-3 py-2">%</th>
                        <th className="text-right px-3 py-2">Bonus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.drafts.map(d => (
                        <tr key={d.id} className="border-t hover:bg-[var(--hover)]">
                          <td className="px-3 py-1.5 text-xs">{d.empleado_nombre}</td>
                          <td className="px-3 py-1.5 text-[10px] text-[var(--ink-muted)] font-mono-num">{d.base_ref}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{eur(d.bruto)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num font-semibold">{eur(d.neto)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{eur(d.ss_empresa)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num font-semibold text-rose-700">{eur(d.coste_empresa_total)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num text-xs">{d.aplicado_pct ? `+${d.aplicado_pct}%` : "—"}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num text-xs">{d.aplicado_bonus ? eur(d.aplicado_bonus) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-xs text-[var(--ink-muted)]">
                <Info size={11} className="inline mr-1"/>
                Borrador guardado en la colección <code className="bg-[var(--bg)] px-1 rounded">nominas_draft</code>. Se reemplazará automáticamente cuando importes la nómina real del mes.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
